import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { generateId, warps, wefts } from 'adacad-drafting-lib';
import { Auth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, Unsubscribe, User } from 'firebase/auth';
import { DatabaseReference, DataSnapshot, equalTo, get, onChildAdded, onChildChanged, onChildRemoved, onValue, orderByChild, push, query, ref, remove, set, update } from 'firebase/database';
import { BehaviorSubject, Subject } from 'rxjs';
import { DraftNodeProxy, FileMeta, FilesState, SaveObj, ShareObj, UserFile } from '../model/datatypes';
import { ErrorBroadcasterService } from './error-broadcaster.service';
import { auth as firebaseAuth, db as firebaseDb } from './firebase-app';
import { defaults as appDefaults } from '../model/defaults';
/**
 * A service to streamline interactions with firebase
 */
@Injectable({
  providedIn: 'root'
})
export class FirebaseService implements OnDestroy {


  //CONNECTIVITY
  private db = firebaseDb;
  private ngZone = inject(NgZone);
  private errorBroadcaster = inject(ErrorBroadcasterService);


  connectedRef = ref(this.db, ".info/connected");
  private connectionChangeEvent = new BehaviorSubject<boolean>(false);
  connectionChangeEvent$ = this.connectionChangeEvent.asObservable();


  //USER AUTHENTICATION
  auth: Auth = firebaseAuth;
  private authChangeEvent = new BehaviorSubject<User | null>(null);
  authChangeEvent$ = this.authChangeEvent.asObservable();
  private unsubscribeAuth: Unsubscribe;
  private unsubscribeConnected: Unsubscribe;


  //DATABASE - USER DATA (list of file_ids with metadata, last logged in time)
  private unsubscribeUserFiles: Unsubscribe[] = [];
  private userFilesChangeEvent = new BehaviorSubject<FilesState>({ user: [], shared: [], public: [] });
  userFilesChangeEvent$ = this.userFilesChangeEvent.asObservable();


  file_list: FilesState = {
    user: [], //all files associated with this user 
    shared: [], //metadata for any shared files
    public: []
  }




  //DATABASE - SHARED DATA (indexed by id, name, img, license, credit, owner uid, owner url, make public)
  sharedFiles = query(ref(this.db, 'shared'));
  private unsubscribeSharedFiles: Unsubscribe[] = [];
  private sharedFilesChangeEvent = new BehaviorSubject<FilesState>({ user: [], shared: [], public: [] });
  sharedFilesChangeEvent$ = this.sharedFilesChangeEvent.asObservable();





  saveTimeUpdatedEvent = new BehaviorSubject<number>(0);
  saveTimeUpdatedEvent$ = this.saveTimeUpdatedEvent.asObservable();

  constructor() {

    // CHECK FOR CONNECTION EVENT 
    this.unsubscribeConnected = onValue(this.connectedRef, this.runInZone((snap) => {
      this.emitConnectionEvent(snap.val())

      if (snap.val() === true) {
        console.log("connected");
      } else {
        console.log("not connected");
      }
    }));


    // CHECK FOR USER EVENTS 

    this.unsubscribeAuth = onAuthStateChanged(this.auth, this.runInZone((aUser: User | null) => {
      //handle user state changes here. Note, that user will be null if there is no currently logged in user.
      this.emitAuthEvent(aUser);
      this.detachUserFileListeners();

      if (aUser) {

        const userFiles = query(ref(this.db, 'users/' + aUser.uid + '/files'), orderByChild('timestamp'));

        this.unsubscribeUserFiles.push(
          onChildAdded(userFiles, this.runInZone((childsnapshot) => {

            if (childsnapshot === undefined || childsnapshot === null) return;
            const added_key: string = childsnapshot.key ?? '';
            if (this.file_list.user.find(el => el.id === parseInt(added_key)) === undefined) {

              const meta = childsnapshot.val();
              var dateFormat = new Date(meta.timestamp);
              meta.date = dateFormat.toLocaleDateString();

              const user_file: UserFile = {
                id: parseInt(added_key),
                meta: meta,
              }
              this.file_list.user.unshift(user_file);
            }
            this.emitUserFilesEvent(this.file_list);

          })),

          onChildChanged(userFiles, this.runInZone((childsnapshot) => {

            if (childsnapshot === undefined || childsnapshot === null) return;
            const ndx = this.file_list.user.findIndex(el => el.id === parseInt(childsnapshot.key ?? '0'));
            if (ndx !== -1) {
              const meta = childsnapshot.val();
              var dateFormat = new Date(meta.timestamp);
              meta.date = dateFormat.toLocaleDateString();
              this.file_list.user[ndx].meta = meta;

            }
            this.emitUserFilesEvent(this.file_list);
          })),

          onChildRemoved(userFiles, this.runInZone((childsnapshot) => {
            if (childsnapshot === undefined || childsnapshot === null) return;
            const removed_key: string = childsnapshot.key ?? '';
            this.file_list.user = this.file_list.user.filter(el => el.id !== parseInt(removed_key))
            this.emitUserFilesEvent(this.file_list);
          }))
        );
      }
    }));

    //SETUP COLLECTION OF SHARED FILES (DOES NOT REQUIRE LOGIN)
    const sharedFiles = query(ref(this.db, 'shared'));

    this.unsubscribeSharedFiles.push(
      onChildAdded(sharedFiles, this.runInZone((childsnapshot) => {
        if (childsnapshot === undefined || childsnapshot === null) return;
        let obj: ShareObj = {
          id: +(childsnapshot.key ?? 0),
          license: childsnapshot.val().license,
          owner_uid: childsnapshot.val().owner_uid,
          owner_url: childsnapshot.val().owner_url,
          owner_creditline: childsnapshot.val().owner_creditline,
          filename: childsnapshot.val().filename,
          desc: childsnapshot.val().desc,
          img: childsnapshot.val().img,
          public: childsnapshot.val().public
        }

        //mark this as shared in the user's list
        if (this.auth.currentUser && childsnapshot.val().owner_uid == this.auth.currentUser.uid) {
          const found_index = this.file_list.shared.findIndex(el => el.id === obj.id);
          if (found_index === -1) {
            this.file_list.shared.push(obj)
          } else {
            this.file_list.shared[found_index] = obj;
          }
          this.emitSharedFilesEvent(this.file_list);
        }
        if (childsnapshot.val().public) {
          const found_index = this.file_list.public.findIndex(el => el.id === obj.id);
          if (found_index === -1) {
            this.file_list.public.push(obj)
          } else {
            this.file_list.public[found_index] = obj;
          }
          this.emitSharedFilesEvent(this.file_list);

        }

      })),

      onChildChanged(sharedFiles, this.runInZone((childsnapshot) => {

        if (childsnapshot === undefined || childsnapshot === null) return;
        let obj: ShareObj = {
          id: +(childsnapshot.key ?? 0),
          license: childsnapshot.val().license,
          owner_uid: childsnapshot.val().owner_uid,
          owner_url: childsnapshot.val().owner_url,
          owner_creditline: childsnapshot.val().owner_creditline,
          filename: childsnapshot.val().filename,
          desc: childsnapshot.val().desc,
          img: childsnapshot.val().img,
          public: childsnapshot.val().public
        }

        //mark this as shared in the user's list
        if (childsnapshot.val().owner_uid == this.auth.currentUser?.uid) {
          const found_index = this.file_list.shared.findIndex(el => el.id === obj.id);
          if (found_index === -1) {
            this.file_list.shared.push(obj)
          } else {
            this.file_list.shared[found_index] = obj;
          }
          this.emitSharedFilesEvent(this.file_list);


        }
        if (childsnapshot.val().public) {
          const found_index = this.file_list.public.findIndex(el => el.id === obj.id);
          if (found_index === -1) {
            this.file_list.public.push(obj)
          } else {
            this.file_list.public[found_index] = obj;
          }
          this.emitSharedFilesEvent(this.file_list);
        } else {
          // File was previously public; remove it from the public list
          const before_len = this.file_list.public.length;
          this.file_list.public = this.file_list.public.filter(el => el.id !== obj.id);
          if (this.file_list.public.length !== before_len) {
            this.emitSharedFilesEvent(this.file_list);
          }
        }


      })),

      //needs to redraw the files list 
      onChildRemoved(sharedFiles, this.runInZone((removedItem) => {

        const removed_key: string = removedItem.key ?? '';
        if (removedItem.key === undefined || removedItem.key === null) return;
        const removed_id = parseInt(removed_key);
        this.file_list.shared = this.file_list.shared.filter(el => el.id !== removed_id);
        this.file_list.public = this.file_list.public.filter(el => el.id !== removed_id);
        this.emitSharedFilesEvent(this.file_list);


      }))
    );




  }

  private runInZone<T>(callback: (value: T) => void): (value: T) => void {
    return (value: T) => {
      this.ngZone.run(() => callback(value));
    };
  }

  private detachUserFileListeners() {
    this.unsubscribeUserFiles.forEach(unsub => unsub());
    this.unsubscribeUserFiles = [];
  }








  /**
   * EMITTERS
   * @param data 
   */

  emitConnectionEvent(data: boolean) {
    this.connectionChangeEvent.next(data);
  }

  /**
   * Annouces changes in the auth state to components whose functionality depends on state 
   * @param data 
   */
  emitAuthEvent(user: User | null) {
    this.authChangeEvent.next(user)
  }

  // FILE FUNCTIONS

  emitUserFilesEvent(data: FilesState) {
    this.userFilesChangeEvent.next(data)
  }

  emitSharedFilesEvent(files: FilesState) {
    this.sharedFilesChangeEvent.next(files)
  }



  /**
   * AUTH
   */

  getUsername(): string {
    return (this.auth.currentUser?.displayName ?? 'no current user');
  }

  logout() {
    signOut(this.auth).then(() => {
      console.log("SUCCESFULLY SIGNED OUT")
    }).catch(err => {
      console.error("Could Not Sign Out")
    })
  }

  login() {
    return signInWithPopup(this.auth, new GoogleAuthProvider());
  }


  //DATABASE READ/WRITE

  /**
  * takes the current state, gives it a new file ID and pushes it to the database
  * @returns the id of the file
  */
  duplicate(ada: SaveObj, meta: FileMeta): Promise<number> {

    if (meta.time == undefined) meta.time = Date.now();
    meta.id = generateId(8);
    const fns = [this.writeFileData(meta.id, ada), this.writeFileMetaData(meta)];
    return Promise.all(fns).then(success => {
      return Promise.resolve(meta.id);
    }).catch(err => {
      return Promise.reject(err);
    });
  }

  updateLastOpened(fileid: number): Promise<boolean> {
    if (!this.auth.currentUser) return Promise.resolve(false);

    const user_ref = ref(this.db, 'users/' + this.auth.currentUser.uid);
    return update(user_ref, { last_opened: fileid })
      .then(success => { return Promise.resolve(true) })
      .catch(err => { return Promise.reject(err) })

  }

  public getFileSize(name: string, obj: any): number {
    const str = JSON.stringify(obj);
    const size = new Blob([str]).size;
    // console.log(name + " is ", size);
    return size;

  }



  /**
   * this is called each time a meaningful change has been made in a file, 
   * usually at the same time that it is written into the timeline
   * @param fileid 
   * @param cur_state 
   */
  updateFile(cur_state: SaveObj, meta: FileMeta): Promise<boolean> {

    console.log("UPDATING FILE ", cur_state, meta)

    if (this.auth.currentUser == null) return Promise.reject('not logged in')
    this.getFileSize("version", cur_state.version);
    this.getFileSize("workspace", cur_state.workspace);
    this.getFileSize("type", cur_state.type);
    this.getFileSize("nodes", cur_state.nodes);
    this.getFileSize("tree", cur_state.tree);
    this.getFileSize("draft nodes", cur_state.draft_nodes);
    this.getFileSize("ops", cur_state.ops);
    this.getFileSize("notes", cur_state.notes);
    this.getFileSize("materials", cur_state.materials);
    this.getFileSize("indexed_image_data", cur_state.indexed_image_data);
    // console.log('DRAFT NODES # ', cur_state.draft_nodes.length);
    // console.log('DRAFT NODES Values', cur_state.draft_nodes);

    if (this.getFileSize("file", cur_state) > 16000000) return Promise.reject("file size is too large to write")

    //do a quick correction for any undefined loom settings
    cur_state.draft_nodes.forEach(dn => {
      if (dn.loom_settings == undefined) {
        dn.loom_settings = appDefaults.loom_settings;
      }
    })



    return this.writeFileData(meta.id, cur_state)
      .then(success => {
        return this.writeFileMetaData(meta)
      })
      .then(success => {
        this.saveTimeUpdatedEvent.next(Date.now());
        return this.updateSaveTime(meta.id)
      })
      .then(success => {
        return this.updateLastOpened(meta.id);
      })
      .catch(err => {
        return Promise.reject(err);
      })
  }



  /**
   * Firebase Realtime Database rejects undefined anywhere in update/set payloads.
   * Recursively omit undefined object keys and replace undefined array slots with null.
   */
  private sanitizeForFirebase(value: any): any {
    if (value === undefined) {
      return null;
    }
    if (value === null || typeof value !== 'object') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map(el => this.sanitizeForFirebase(el));
    }
    const out: Record<string, any> = {};
    for (const key of Object.keys(value)) {
      const v = value[key];
      if (v === undefined) continue;
      out[key] = this.sanitizeForFirebase(v);
    }
    return out;
  }

  private writeLowDataItems(fileid: number, partial: any): Promise<boolean> {
    const filepath_base = ref(this.db, 'filedata/' + fileid + '/ada/');

    return update(filepath_base, this.sanitizeForFirebase(partial))
      .then(success => { return Promise.resolve(true) })
      .catch(err => { return Promise.reject(err) })
  }

  /**
   * this function writes each seed draft individually in order to prevent unneccessary write too large errors
   * @param fileid 
   * @param drafts 
   * @returns 
   */
  private async writeDrafts(fileid: number, drafts: Array<DraftNodeProxy>): Promise<any> {

    console.log("WRITING DRAFTS ", drafts);
    if (drafts === undefined || drafts === null || drafts.length === 0) return Promise.resolve(true);

    drafts.forEach((draft: DraftNodeProxy) => {
      if (draft.compressed_draft !== undefined) {
        const size = draft.compressed_draft.warps * draft.compressed_draft.wefts;
        if (size > 100000) {
          if (draft.loom_settings !== undefined && draft.loom !== null && draft.loom_settings.type != 'jacquard') {
            draft.compressed_draft.compressed_drawdown = [];
          } else {
            this.errorBroadcaster.postError(-1, 'FILE_ERROR', "a Draft is too large to write");
          }
        }
      }
    });




    //get a list of the current drafts
    const draft_ref = ref(this.db, 'filedata/' + fileid + '/ada/draft_nodes/');
    return set(draft_ref, this.sanitizeForFirebase(drafts))
      .then(success => { return Promise.resolve(true) })
      .catch(err => { return Promise.reject(err) })

  }

  /**
   * break this into groups
   * @param fileid 
   * @param cur_state 
   * @returns 
   */
  private writeFileData(fileid: number, cur_state: SaveObj): Promise<boolean> {


    //save the small things:
    const small_things = {
      version: cur_state.version,
      workspace: cur_state.workspace,
      zoom: cur_state.zoom,
      type: cur_state.type,
      nodes: cur_state.nodes,
      tree: cur_state.tree,
      ops: cur_state.ops,
      notes: cur_state.notes,
      materials: cur_state.materials,
      indexed_image_data: cur_state.indexed_image_data,
    }

    const small_things_fn = this.writeLowDataItems(fileid, small_things);
    const drafts_fn = this.writeDrafts(fileid, cur_state.draft_nodes);
    return Promise.all([small_things_fn, drafts_fn])
      .then(success => { return Promise.resolve(true) })
      .catch(err => { return Promise.reject(err) })
  }




  private writeFileMetaData(meta: FileMeta): Promise<boolean> {
    const current_user = this.auth.currentUser;
    if (current_user === undefined || current_user === null) return Promise.reject("no current user");
    let user_path = ref(this.db, 'users/' + current_user.uid + '/files/' + meta.id);


    if (meta.from_share == undefined || meta.from_share == null) meta.from_share = '';
    if (meta.time == undefined || meta.time == null) meta.time = Date.now();

    return set(user_path, this.sanitizeForFirebase({
      name: meta.name,
      desc: meta.desc,
      timestamp: meta.time,
      from_share: meta.from_share
    }))
      .then(success => { return Promise.resolve(true) })
      .catch(err => { return Promise.reject(err) })
  }


  /**
   * only called from filebrowser when we need to rename a file (that may not be our current file)
   * @param meta 
   * @returns 
   */
  renameUserFile(meta: FileMeta): Promise<boolean> {
    const current_user = this.auth.currentUser;
    if (current_user === undefined || current_user === null) return Promise.reject("no current user");
    let user_path = ref(this.db, 'users/' + current_user.uid + '/files/' + meta.id);


    if (meta.from_share == undefined || meta.from_share == null) meta.from_share = '';

    return update(user_path, {
      name: meta.name,
    })
      .then(success => { return Promise.resolve(true) })
      .catch(err => { return Promise.reject(err) })
  }


  /**
 * usually called after new data is written, this updates the time at which the file was updated and makes sure the current file id is the one that is saved as the last file opened. 
 * @param fileid 
 * @returns 
 */
  private updateSaveTime(fileid: number): Promise<boolean> {

    if (!this.auth.currentUser) return Promise.resolve(false);

    let user_path = ref(this.db, 'users/' + this.auth.currentUser.uid + '/files/' + fileid);
    const stamp = Date.now();
    return update(user_path, { timestamp: stamp, })
      .then(res => { return Promise.resolve(true) })
      .catch(err => { return Promise.reject(err) })
  }


  //a variant to check if a file exists before calling getShare and throwing an error if/when it doesn't exist
  hasSharedFile(fileid: number): Promise<boolean> {
    console.log("CHECKING IF SHARED FILE EXISTS", fileid);
    return get(ref(this.db, `shared/${fileid}`))
      .then((shareobj) => {
        console.log("SHARED FILE EXISTS", shareobj.exists());
        return Promise.resolve(shareobj.exists());
      }).catch(e => { return Promise.resolve(false) })
  }

  /**
  * gets the share Object at a given id
  * @returns the file data
  */
  getShare(fileid: number): Promise<ShareObj> {
    console.log("GETTING SHARED FILE", fileid);
    return get(ref(this.db, `shared/${fileid}`))
      .then((shareobj) => {

        if (shareobj.exists()) {
          return Promise.resolve(shareobj.val());
        } else {
          this.errorBroadcaster.postError(-1, 'FILE_ERROR', "This Shared File Cannot be Found")
          return Promise.reject("Shared File Not Found")
        }
      }).catch(e => { console.error(e) });

  }



  /**
   * gets the file at a given id
   * @returns the file data
   */
  getFile(fileid: number): Promise<SaveObj> {

    return get(ref(this.db, `filedata/${fileid}`))
      .then((filedata) => {

        if (filedata.exists()) {
          return Promise.resolve(filedata.val().ada);
        } else {
          this.errorBroadcaster.postError(-1, 'FILE_ERROR', "This File Cannot be Found")
          return Promise.reject("User found but file id not found")
        }
      }).catch(e => { console.error(e) });

  }

  /**
   * gets the file meta for a given id. 
   * @param fileid 
   * @returns the meta data
   */
  getFileMeta(fileid: number): Promise<FileMeta> {


    const current_user = this.auth.currentUser;
    if (current_user === undefined || current_user === null) return Promise.reject("no current user");
    const file_path = ref(this.db, 'users/' + current_user.uid + '/files/' + fileid);
    return get(file_path).then((meta) => {

      if (meta.exists()) {
        let obj: FileMeta = {
          id: fileid,
          desc: meta.val().desc,
          time: meta.val().timestamp,
          name: meta.val().name,
          from_share: (meta.val().from_share == undefined) ? '' : meta.val().from_share,
          share_owner: (meta.val().share_owner == undefined) ? '' : meta.val().share_owner,
        };
        return Promise.resolve(obj);
      } else {
        return Promise.reject("No meta data found for file id " + fileid)
      }

    });
  }

  /**
  * calls when a file is selected to be deleted from the files list
  * deletes all references to the file and then deletes from the users file list
  * @param fileid 
  * @returns 
  */
  removeFile(fileid: number) {

    remove(ref(this.db, `filedata/${fileid}`));

    const current_user = this.auth.currentUser;
    if (current_user === undefined || current_user === null) return;
    remove(ref(this.db, 'users/' + current_user.uid + '/files/' + fileid));

  }


  /**
   * creates a new reference for a shared file 
   * @param file_id 
   * @param share_data 
   * @returns 
   */
  createSharedFile(file_id: string, share_data: ShareObj): Promise<string> {

    console.log("CREATING SHARED FILE", file_id, share_data);
    const shared_ref = ref(this.db, 'shared/' + file_id);

    return set(shared_ref, this.sanitizeForFirebase(share_data))
      .then(success => {
        console.log("SHARED FILE CREATED", success);
        return Promise.resolve(file_id)

      })
      .catch(err => {
        console.error(err);
        return Promise.reject("could not create new shared item")
      })
  }


  /**
   * called when a user changes the license for a shared file. 
   * @param fileid 
   * @param license 
   * @returns 
   */
  updateSharedFile(fileid: string, share: ShareObj): Promise<boolean> {

    const shared_file_ref = ref(this.db, 'shared/' + fileid);

    return update(shared_file_ref, this.sanitizeForFirebase(share))
      .then(success => {
        return Promise.resolve(true);
      })
      .catch(err => {
        console.error(err);
        return Promise.reject(false);
      })
  }


  /**
   * called when a user changes the license for a shared file. 
   * @param fileid 
   * @param license 
   * @returns 
   */
  updateSharedLicense(fileid: string, license: string): Promise<boolean> {

    const shared_file_ref = ref(this.db, 'shared/' + fileid);

    return update(shared_file_ref, { license: license })
      .then(success => {
        return Promise.resolve(true);
      })
      .catch(err => {
        console.error(err);
        return Promise.reject(false);
      })


  }

  /**
  * The shared entry is not the same as the file. This operation removes the entry to this file in the shared this.db but the file that was shared still exists in the files this.db. This will automatically rename that file to reflect that it used to be shared. 
  * @returns the file data
   */
  removeSharedFile(file_id: string): Promise<boolean> {

    const current_user = this.auth.currentUser;

    if (current_user === undefined || current_user === null) return Promise.reject("no current user");
    let int_id: number = +file_id;
    remove(ref(this.db, `shared/${file_id}`)).catch(err => { return Promise.reject(err) })

    return this.getFileMeta(int_id).then(meta => {
      return update(ref(this.db, 'users/' + current_user.uid + '/files/' + file_id), { name: meta.name + "(previously shared)" })
        .then(val => { return Promise.resolve(true) })
        .catch(err => { return Promise.reject(err) })
    })
      .catch(err => { return Promise.reject(err) })



  }


  /**
  * checks to see if this user has an id already saved for their last used file
  * @param user 
  */
  getMostRecentFileIdFromUser(): Promise<number> {
    if (this.auth.currentUser === undefined || this.auth.currentUser === null) return Promise.reject("no current user");
    const user_ref = ref(this.db, `users/${this.auth.currentUser.uid}`);
    return get(user_ref).then((userdata) => {
      if (userdata.exists()) {
        return Promise.resolve(userdata.val().last_opened);
      } else {
        return Promise.reject("no last opened value found for the current user");
      }
    }).catch(err => {
      return Promise.reject(err);
    })
  }


  ngOnDestroy() {
    this.unsubscribeAuth?.();
    this.unsubscribeConnected?.();
    this.detachUserFileListeners();
    this.unsubscribeSharedFiles.forEach(unsub => unsub());
  }

}

